/**
 * The card a session's name opens on the item page: the session's five steps,
 * S1 to S5, read for the item the page is about, so a person can follow how the
 * session got from who was in the room to where this item landed.
 *
 *   <ObjectRef kind="session" name={…} href={…}
 *     summary={<SessionStepsCard sessionId={…} itemId={…} rank={3} outOf={12} href={…} />} />
 *
 * Drawn only while the card is open (ds/Popover), so a session not yet in the
 * store is read then, not when the page loads.
 */
import { useEffect, useState } from 'react';
import { PopoverRows } from '@/components/ds';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { summarizeSessionSteps } from './sessionStepsSummary';

export interface SessionStepsCardProps {
  sessionId: string;
  itemId: string;
  rank?: number | null;
  outOf?: number | null;
  href: string;
  nameOf?: (userId: string) => string | null | undefined;
}

export function SessionStepsCard({ sessionId, itemId, rank, outOf, href, nameOf }: SessionStepsCardProps) {
  const session = useSessionStore((s) => s.sessions.find((x) => x.id === sessionId));
  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const attributes = useAttributesStore((s) => s.attributes);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (session) return;
    let live = true;
    void fetchSessionById(sessionId).then((found) => {
      if (live && !found) setMissing(true);
    });
    return () => {
      live = false;
    };
  }, [session, sessionId, fetchSessionById]);

  const heading = 'How this session reached its ranking';
  const link = { label: 'Open the session', href };
  if (!session) {
    return (
      <PopoverRows
        heading={heading}
        rows={[]}
        footnote={missing ? 'This session could not be read.' : 'Reading the session…'}
        link={link}
      />
    );
  }

  const byKey = new Map(attributes.map((a) => [a.key, a] as const));
  const lines = summarizeSessionSteps(session, { itemId, rank, outOf, nameOf, attributeFor: (id) => byKey.get(id) });
  return (
    <PopoverRows
      heading={heading}
      rows={lines.map((l) => ({
        label: l.label,
        value: l.value,
        state: l.state,
        note:
          l.detail || l.note ? (
            <>
              {l.detail && <div>{l.detail}</div>}
              {l.note && <div>{l.note}</div>}
            </>
          ) : undefined,
      }))}
      link={link}
    />
  );
}
